import { Log, User, UserManager } from 'oidc-client';
import Config, { adminScopes } from './config';

export class AuthService {
  public userManager: UserManager;

  constructor() {
    const clientId = Config.auth0ClientId();
    if (clientId.startsWith('REPLACE_WITH_')) {
      // infra-auth0#371 provisions the real per-environment client IDs; until that's applied and
      // Config.auth0ClientId() is filled in, fail loudly at construction time instead of letting
      // Auth0 reject an obviously-invalid client_id with a confusing error deep in the redirect
      // flow. See PLT-1180.
      throw new Error(
        `AuthService: auth0ClientId() returned a placeholder value (${clientId}). ` +
          'infra-auth0#371 must be applied and the real client ID filled in before this can run.',
      );
    }

    this.userManager = new UserManager({
      authority: `https://${Config.auth0Domain()}/`,
      client_id: clientId,
      redirect_uri: Config.appUrl(),
      post_logout_redirect_uri: Config.appUrl(),
      silent_redirect_uri: `${Config.appUrl()}silent-renew.html`,
      scope: ['openid', ...adminScopes].join(' '),
      response_type: 'code',
      loadUserInfo: false,
      revokeAccessTokenOnSignout: true,
      automaticSilentRenew: true,
      extraQueryParams: { audience: Config.auth0Audience() },
    });

    Log.logger = console;
    Log.level = Log.INFO;
  }

  public getUser(): Promise<User | null> {
    return this.userManager.getUser();
  }

  public login(): Promise<void> {
    this.userManager.signinRedirect();
    return Promise.resolve();
  }

  public async completeLogin(): Promise<User> {
    try {
      return await this.userManager.signinRedirectCallback();
    } finally {
      // oidc-client consumes and removes the matching state from its own store, but never
      // touches the URL itself — the ?code=...&state=... query params stay there for the rest
      // of the session. If the Login page ever re-mounts later (e.g. identity gets cleared for
      // any reason), its useEffect sees a non-empty `code` and reprocesses this same,
      // already-consumed value, failing with "No matching state found in storage". Always strip
      // it, whether this call succeeded or failed. Only remove `code`/`state` (not the whole
      // query string) — the app re-derives `initData` from the URL on every load (see
      // reducer/index.tsx), so wiping other params here would lose the admin/customer selection
      // that launched this session. Build the URL from location.href (not Config.appUrl()) so
      // this can never throw a cross-origin SecurityError if appUrl() and the serving origin
      // ever diverge.
      const url = new URL(window.location.href);
      url.searchParams.delete('code');
      url.searchParams.delete('state');
      window.history.replaceState({}, document.title, `${url.pathname}${url.search}${url.hash}`);
    }
  }

  public renewToken(): Promise<User> {
    return this.userManager.signinSilent();
  }

  public logout(): Promise<void> {
    return this.userManager.signoutRedirect();
  }
}

// automaticSilentRenew registers background timers/listeners on the UserManager, so
// getAuthService() must return one shared instance rather than constructing a fresh
// AuthService (and UserManager) on every call — otherwise every call site (login, completeLogin,
// renewToken) would spin up its own independent silent-renew loop.
let instance: AuthService | null = null;

export const getAuthService = (): AuthService => {
  if (!instance) {
    instance = new AuthService();
  }
  return instance;
};

// Test-only: clears the singleton so each test that calls getAuthService() gets a fresh
// AuthService/UserManager instead of observing state left over from a previous test.
export const _resetAuthServiceForTesting = (): void => {
  instance = null;
};
